import { CircularProgress, Grid } from '@epilot/journey-elements'
import type { EpilotControlProps } from '@epilot/journey-logic-commons'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useJourneyContext, useStepBlockId } from '../../../utils'
import type { MeterEntityWithValidation } from '../../../utils/clients/entity-client'
import { includeCommon } from '../../../utils/includeCommon'
import { MeterCardDivider } from '../MeterReadingControl/components/MeterCardDivider'
import { MeterCardHeader } from '../MeterReadingControl/components/MeterCardHeader'
import { useGetContracts } from '../MeterReadingControl/useGetContracts'

import { CounterReadingForm } from './CounterReadingForm'
import { MeterReadingOptionalFields } from './MeterReadingOptionalFields'
import type {
  CounterReading,
  MeterReadingDynamicControlDataMeters,
  MeterReadingDynamicControlOptions,
  DynamicMeterReadingFormValues
} from './types'
import {
  getUniqueContractMeters,
  getUniqueContractNames,
  isMeterBlockDataValid,
  isMeterDataValid
} from './utils'

/**
 * This control would load the meters and counters for the selected contracts and display the form for the user to fill in the meter readings.
 * The hook used to load contracts will also load the meter readings validation rules (aka, last known reading, so the block should allow lower value than that)
 * it is possible that the user has multiple contracts with multiple meters and counters
 * it is possible that there are contracts that share the same meter
 * it is possible that there are contracts sharing the same name
 * (the end customer has 2 contracts, one for water and the other for waste water, but the contract names are the same from the prospective of the business)
 */
function MeterReadingDynamicControl(props: EpilotControlProps) {
  const { uischema, data, path, handleChange, errors } = props

  const stepBlockId = useStepBlockId(path)

  const options = uischema.options as MeterReadingDynamicControlOptions
  const { fields } = options
  const { context } = useJourneyContext()
  const { t } = useTranslation()

  const { contracts, isLoading } = useGetContracts({
    shouldCheck: true,
    journeySteps: context.journey.steps,
    stepsHistoryStateArray: context._stepsHistoryStateArray,
    contextEntities: context._contextEntitiesData || {},
    isPreview: context._isPreview
  })

  const allMetersUnique = useMemo(
    () => getUniqueContractMeters(contracts),
    [contracts]
  )

  const onChangeMeterData = (
    meterId: string,
    value: DynamicMeterReadingFormValues
  ) => {
    if (!value.readingDate) {
      value.readingDate = new Date()
    }
    const oldData = (data as MeterReadingDynamicControlDataMeters)?.meters
    const newData = Array.isArray(oldData) ? [...oldData] : []
    const existingIndex = newData.findIndex((m) => m.meterId === meterId)

    if (existingIndex > -1) {
      newData[existingIndex] = value
    } else {
      newData.push(value)
    }

    handleChange(path, {
      meters: newData,
      _isValid: isMeterBlockDataValid(newData, contracts)
    })
  }

  const renderMeterReadingForm = (meter: MeterEntityWithValidation) => {
    // if contracts where found in the journey, but no meters were found, show loading
    if (!Array.isArray(meter.counters) || meter.counters.length === 0) {
      return (
        <div>{t('meter_reading.counters_error', 'No counters were found')}</div>
      )
    }

    const existingMeterData = (
      data as MeterReadingDynamicControlDataMeters
    )?.meters?.find((m) => m.meterId === meter._id)

    const readingsData = existingMeterData?.readings || []

    return (
      <>
        <MeterCardHeader slug="meter" title={meter.meter_number} />
        <Grid container spacing={2}>
          {meter.counters.map((counter) => {
            const oldReading = meter.validation?.find(
              (r) => r.counter_id === counter._id
            )

            return (
              <CounterReadingForm
                counterEntity={counter}
                handleChange={(value) => {
                  const newReadings = existingMeterData?.readings
                    ? [...existingMeterData.readings]
                    : []

                  const existingIndex = newReadings.findIndex(
                    (cont: CounterReading) => cont.counterId === value.counterId
                  )

                  if (existingIndex > -1) {
                    newReadings[existingIndex] = value
                  } else {
                    newReadings.push(value)
                  }

                  onChangeMeterData(meter._id, {
                    ...existingMeterData,
                    readings: newReadings,
                    meterId: meter._id,
                    _meter_number: meter.meter_number,
                    _isValid: isMeterDataValid(
                      {
                        ...existingMeterData,
                        readings: newReadings,
                        meterId: meter._id
                      },
                      fields,
                      meter.counters.length,
                      meter.validation
                    )
                  })
                }}
                hasError={!!errors}
                id={stepBlockId}
                key={'counter' + counter._id}
                oldReading={oldReading}
                value={readingsData.find((r) => r.counterId === counter._id)}
              />
            )
          })}
          <MeterReadingOptionalFields
            fields={fields}
            handleChange={(value) => {
              onChangeMeterData(meter._id, {
                ...existingMeterData,
                ...value,
                meterId: meter._id,
                _meter_number: meter.meter_number,
                _isValid: isMeterDataValid(
                  {
                    ...existingMeterData,
                    ...value,
                    meterId: meter._id
                  },
                  fields,
                  meter.counters.length,
                  meter.validation
                )
              })
            }}
            hasError={!!errors}
            id={stepBlockId}
            value={existingMeterData as never}
          />
        </Grid>
      </>
    )
  }

  if (isLoading) {
    return (
      <center>
        <CircularProgress />
      </center>
    )
  }
  // if not contracts where found in the journey, show error
  else if (contracts.length === 0 && !isLoading) {
    return (
      <div>
        {t('meter_reading.contracts_error', 'No contract was selected')}
      </div>
    )
  }

  return (
    <>
      {
        // if no meters, then display a message
        contracts
          .filter(
            (contract) =>
              !Array.isArray(contract.meters) || contract.meters.length === 0
          )
          .map((contractWithoutMeters) => {
            return (
              <div key={contractWithoutMeters._id}>
                {`(${contractWithoutMeters.contract_name}): ${t(
                  'meter_reading.meters_error',
                  'No meters were found for the contract'
                )}`}
              </div>
            )
          })
      }
      {allMetersUnique.map((meter, index) => {
        const meterContracts = contracts.filter((contract) =>
          contract.meters.find((m) => m._id === meter._id)
        )
        const uniqueContractNames = getUniqueContractNames(meterContracts)

        return (
          <>
            {index > 0 && <MeterCardDivider />}
            <div key={'meter' + meter._id}>
              <MeterCardHeader
                slug="contract"
                title={uniqueContractNames.join(' & ')}
              />

              {renderMeterReadingForm(meter)}
            </div>
          </>
        )
      })}
    </>
  )
}
export default withJsonFormsControlProps(
  includeCommon({ component: MeterReadingDynamicControl }) as never
)
